import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { getCached, setCached, CACHE_KEYS } from "../utils/sessionCache";
import { getTnStatus } from "../lib/tiendaNube";
import { loadOnboardingSeen, isSeen, markSeen } from "../lib/onboardingSeen";

/** Cuál de los banners promocionales de home corresponde mostrar (o ninguno). */
export type HomeBannerKind = "upsell" | "brand" | "tiendanube" | null;

/** Estado de la suscripción que amerita un banner informativo en la home. */
export type SubscriptionStatus = "cancelled" | "paused" | null;

export type HomeBannersState = {
  /**
   *  - "upsell": usuario de prueba gratis que ya generó ≥5 imágenes.
   *  - "brand": usuario pago sin análisis de marca (`config.media_agent_analysis`).
   *  - "tiendanube": usuario pago CON análisis hecho, sin Tienda Nube conectada
   *    y que no descartó el banner (post "Mejorá tus fotos").
   *  - null: no corresponde ninguno (o aún cargando).
   */
  kind: HomeBannerKind;
  /** Imágenes gratis restantes (solo relevante para "upsell"). */
  freeImagesRemaining: number | null;
  /** País del usuario === Argentina (cambia el copy/CTA del upsell). */
  isArgentina: boolean;
  /**
   * Estado de la suscripción (INDEPENDIENTE de `kind`):
   *  - "cancelled": hay una cancelación programada a futuro.
   *  - "paused": la suscripción está pausada (reanuda a futuro).
   *  - null: ninguno.
   */
  status: SubscriptionStatus;
  /** Fecha de cancelación formateada (solo si status === "cancelled"). */
  cancelDate: string | null;
  /** Fecha de reanudación formateada (solo si status === "paused"). */
  resumeDate: string | null;
  /**
   * Motivo de la cancelación (`company_profiles.trigger_cancelacion`). Solo
   * relevante si status === "cancelled". Valor conocido: "fondos_insuficientes"
   * (el último pago fue rechazado por falta de fondos) → la UI muestra un copy
   * distinto (por rechazo de pago, con reintento automático). `null` = baja
   * voluntaria u otro motivo (copy default).
   */
  cancelTrigger: string | null;
  /** Descarta el banner de Tienda Nube ("No me interesa") — por usuario, en la DB
   *  (`onboarding_seen`). Lo inyecta el hook; el render lo llama desde el botón. */
  dismissTiendanube?: () => void;
};

const EMPTY: HomeBannersState = {
  kind: null,
  freeImagesRemaining: null,
  isArgentina: false,
  status: null,
  cancelDate: null,
  resumeDate: null,
  cancelTrigger: null,
};

// Mismo formato que el Paywall ("20 de julio de 2026").
const formatDate = (raw: unknown): string | null => {
  if (!raw) return null;
  const d = new Date(raw as string);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("es-AR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const isFuture = (raw: unknown): boolean => {
  if (!raw) return false;
  const d = new Date(raw as string);
  return !isNaN(d.getTime()) && d.getTime() > Date.now();
};

/**
 * Resuelve qué banner de home mostrar, con las MISMAS condiciones que la
 * pantalla clásica de selección de productos. Fuente única para que el clásico
 * y la Interfaz V2 muestren exactamente los mismos banners.
 *
 * Banners promocionales (`kind`, mutuamente excluyentes):
 *  - Plan gratis + ≥5 imágenes generadas → "upsell" (prueba gratis).
 *  - Plan pago + sin `media_agent_analysis` → "brand" (describir la marca),
 *    salvo que `suppressBrand` esté activo.
 *
 * Estado de suscripción (`status`, ortogonal a `kind`):
 *  - Cancelación programada a futuro → "cancelled".
 *  - Reanudación programada a futuro (pausa) → "paused".
 * El consumidor decide si renderizar el banner de estado (ver `HomeBannersView`
 * con `showStatus`); hoy solo se muestra en mobile.
 *
 * @param opts.suppressBrand fuerza ocultar el banner de marca (ej. al volver de
 *   `/redes-sociales/descripcion-brand` con `hideBrandBanner`).
 */
export function useHomeBanners(opts?: {
  suppressBrand?: boolean;
}): HomeBannersState {
  const suppressBrand = opts?.suppressBrand ?? false;
  const [state, setState] = useState<HomeBannersState>(EMPTY);
  const uidRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;
        uidRef.current = user.id;

        const { data: profile } = await supabase
          .from("profiles")
          .select("id_company")
          .eq("id", user.id)
          .single();
        const idCompany = profile?.id_company as string | undefined;
        if (!idCompany) return;

        const { data: company } = await supabase
          .from("company_profiles")
          .select(
            "plan, pais, imagenes_restantes, fecha_cancelacion_programada, fecha_reactivacion_programada, trigger_cancelacion",
          )
          .eq("id_company", idCompany)
          .single();
        if (cancelled) return;

        const plan = (company as any)?.plan || "gratis";
        const pais = ((company as any)?.pais || "") as string;
        const imagenesRestantes =
          typeof (company as any)?.imagenes_restantes === "number"
            ? ((company as any).imagenes_restantes as number)
            : null;
        const isArgentina = pais.toLowerCase() === "argentina";
        const isPaidPlan = plan && plan !== "gratis";

        // Estado de la suscripción (ortogonal al banner promocional). Solo
        // aplica a usuarios pagos; un free no tiene cancelación/pausa.
        const cancelRaw = (company as any)?.fecha_cancelacion_programada;
        const resumeRaw = (company as any)?.fecha_reactivacion_programada;
        const triggerRaw = (company as any)?.trigger_cancelacion;
        let status: SubscriptionStatus = null;
        let cancelDate: string | null = null;
        let resumeDate: string | null = null;
        let cancelTrigger: string | null = null;
        if (isPaidPlan && isFuture(cancelRaw)) {
          status = "cancelled";
          cancelDate = formatDate(cancelRaw);
          cancelTrigger = (triggerRaw as string) || null;
        } else if (isPaidPlan && isFuture(resumeRaw)) {
          status = "paused";
          resumeDate = formatDate(resumeRaw);
        }

        if (!isPaidPlan) {
          // Prueba gratis: upsell tras 5 imágenes generadas (sin contar errores).
          const { count } = await supabase
            .from("imagenes_generadas")
            .select("id", { count: "exact", head: true })
            .neq("has_error", true)
            .eq("id_company", idCompany);
          if (cancelled) return;
          setState({
            kind: (count || 0) >= 5 ? "upsell" : null,
            freeImagesRemaining: imagenesRestantes,
            isArgentina,
            status,
            cancelDate,
            resumeDate,
            cancelTrigger,
          });
          return;
        }

        // Plan pago: banner de marca si todavía no hay análisis de marca.
        if (suppressBrand) {
          setState({
            kind: null,
            freeImagesRemaining: null,
            isArgentina,
            status,
            cancelDate,
            resumeDate,
            cancelTrigger,
          });
          return;
        }
        let hasAnalysis = getCached<boolean>(CACHE_KEYS.COMPANY_CONFIG);
        if (hasAnalysis === null) {
          try {
            const { data: config } = await supabase
              .from("config")
              .select("media_agent_analysis")
              .eq("id_company", idCompany)
              .maybeSingle();
            let computed = !!config?.media_agent_analysis;
            // Fallback robusto: `media_agent_analysis` puede quedar VACÍO ('')
            // aunque el user YA haya completado "Mejorá tus fotos" (el webhook
            // del análisis a veces devuelve vacío). En ese caso igual ocultamos
            // el banner si la marca DEFAULT ya tiene IDENTIDAD cargada (colores
            // o contexto) — así el banner es one-time real: desaparece cuando
            // la marca ya está completada, sin depender de ese flag poco fiable.
            if (!computed) {
              const { data: brand } = await supabase
                .from("brand_profiles")
                .select("colors, context")
                .eq("id_company", idCompany)
                .eq("is_default", true)
                .eq("is_draft", false)
                .limit(1)
                .maybeSingle();
              const colors = (brand as { colors?: unknown } | null)?.colors;
              const ctx = (brand as { context?: unknown } | null)?.context;
              const hasIdentity =
                (Array.isArray(colors) && colors.length > 0) ||
                (typeof ctx === "string" && ctx.trim().length > 0);
              if (hasIdentity) computed = true;
            }
            hasAnalysis = computed;
            setCached(CACHE_KEYS.COMPANY_CONFIG, hasAnalysis);
          } catch {
            hasAnalysis = false;
          }
        }
        if (cancelled) return;
        if (!hasAnalysis) {
          setState({
            kind: "brand",
            freeImagesRemaining: null,
            isArgentina,
            status,
            cancelDate,
            resumeDate,
            cancelTrigger,
          });
          return;
        }

        // Análisis de marca YA hecho → evaluar el banner de Tienda Nube (aparece
        // justo donde antes no quedaba nada, post "Mejorá tus fotos"). Se muestra
        // si el user NO lo descartó ("No me interesa", por usuario) y la empresa
        // NO tiene ninguna tienda conectada.
        await loadOnboardingSeen(user.id);
        if (cancelled) return;
        if (isSeen("tiendanube_banner")) {
          setState({
            kind: null,
            freeImagesRemaining: null,
            isArgentina,
            status,
            cancelDate,
            resumeDate,
            cancelTrigger,
          });
          return;
        }

        let tnConnected = getCached<boolean>(CACHE_KEYS.TN_STATUS);
        if (tnConnected === null) {
          try {
            const { data: brandRow } = await supabase
              .from("brand_profiles")
              .select("id")
              .eq("id_company", idCompany)
              .eq("is_default", true)
              .eq("is_draft", false)
              .limit(1)
              .maybeSingle();
            const bId = (brandRow as { id?: string } | null)?.id ?? null;
            const st = await getTnStatus(bId);
            // `companyHasStores`: si CUALQUIER marca de la empresa ya tiene tienda
            // conectada, no ofrecemos conectar (es por empresa, no por marca).
            tnConnected = st.connected || st.companyHasStores;
            setCached(CACHE_KEYS.TN_STATUS, tnConnected);
          } catch {
            tnConnected = false;
          }
        }
        if (cancelled) return;
        if (tnConnected) {
          // Ya conectó una tienda → marcamos el flag "resuelto" (mismo que
          // "No me interesa") para que el banner NO vuelva nunca, ni si después
          // desconecta. En la próxima carga `isSeen` corta antes (sin consultar
          // el estado de conexión).
          markSeen("tiendanube_banner", uidRef.current);
        }
        setState({
          kind: tnConnected ? null : "tiendanube",
          freeImagesRemaining: null,
          isArgentina,
          status,
          cancelDate,
          resumeDate,
          cancelTrigger,
        });
      } catch {
        if (!cancelled) setState(EMPTY);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [suppressBrand]);

  // "No me interesa": marca el flag por usuario (DB) y oculta el banner ya. En la
  // próxima carga, `isSeen("tiendanube_banner")` lo mantiene oculto.
  const dismissTiendanube = useCallback(() => {
    markSeen("tiendanube_banner", uidRef.current);
    setState((s) => ({ ...s, kind: null }));
  }, []);

  return { ...state, dismissTiendanube };
}
